import { Message, LoadingDots } from '@flos/react-ui';
import { omit, assign } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConfigContext } from '../../../context/ConfigContext';
import { getClaimFormSubmitValue } from '../../../helpers/getClaimFormSubmitValue';
import { getElementFromElementId } from '../../../helpers/getElementFromElementId';
import { SummaryRowTemplate } from '../../../helpers/templates';
import { getBasketItems, getTokensFromEmail, refreshToken } from '../../../services/scalepoint';
import useAppState from '../../../state/appState';
import {
    Scalepointwidget as ScalepointType,
    FormConfig,
    FormValues
} from '@topdanmark-onlinesolutions/claim-form-types';
import { Status } from '../../../types/status';
import * as logger from '../../../utils/logger';
import './styles.scss';

export type ScalepointData = {
    spToken: string;
    spUrl: string;
    ssoToken: string;
    scalepointItemsInBasket: Array<{
        token: string;
        description: string;
        age: number;
        categoryName: string;
        categoryGroupName: string;
        newPrice: number;
        customerDemand: number;
        noDocumentation: false;
        unableToUpload: true;
        brand: string;
        model: string;
    }>;
    tokenlastRefresh: number | undefined;
    error?: boolean;
};

const KEY_IS_SCALEPOINT_CLOSED = 'isScalepointClosed';

// when using this component, the page config should set hasWideContent to true
const Scalepoint = ({ elementId }: ScalepointType) => {
    const appConfig = useContext(ConfigContext);

    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const [componentState, setComponentState] = useState<Status>(Status.LOADING);

    const [clickCount, setClickCount] = useState(0);

    const scalepointData: ScalepointData = Scalepoint.getValue(formValues, elementId);

    const SCALEPOINT_DIV_ID = 'claim_scalepointToken';

    const pollBasketItemsTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const [searchParams] = useSearchParams();

    const IS_SCALEPOINT_CLOSED: boolean | undefined = searchParams.has(KEY_IS_SCALEPOINT_CLOSED)
        ? searchParams.get(KEY_IS_SCALEPOINT_CLOSED) === 'true'
            ? true
            : false
        : appConfig?.features?.isScalepointClosed;

    console.log('# IS_SCALEPOINT_CLOSED=', IS_SCALEPOINT_CLOSED);

    useEffect(() => {
        if (IS_SCALEPOINT_CLOSED) {
            setFormValue(KEY_IS_SCALEPOINT_CLOSED, true);
        } else {
            setFormValue(KEY_IS_SCALEPOINT_CLOSED, false);
        }
    }, [IS_SCALEPOINT_CLOSED, setFormValue]);

    // fetches tokens to be used to fetch the JS script
    useEffect(() => {
        // skip this useEffect if the Scalepoint feature toggle is turned off
        if (IS_SCALEPOINT_CLOSED) {
            return;
        }

        async function fetchSSOToken() {
            if (componentState === Status.ERROR) {
                return;
            }
            if (scalepointData === undefined || scalepointData.error) {
                // get a fresh token
                let email = appConfig.user.getActiveUser().primaryEmail.trim();

                // Rudimentary error handling
                // The dummy email address will be replaced
                // when the Scalepoint basket is submitted serverside
                // as the customer will be asked for his email later in the flow
                if (email === null || !email.length || !email.match(/@/)) {
                    email = 'email@example.com';
                }

                const result = await getTokensFromEmail(appConfig, email);
                if (result !== null) {
                    setFormValue(elementId, {
                        spToken: result.token,
                        spUrl: result.url,
                        ssoToken: result.ssoToken,
                        scalepointItemsInBasket: [],
                        tokenlastRefresh: Date.now()
                    });
                } else {
                    setComponentState(Status.ERROR);
                }
            } else {
                // refresh token
                const SCALEPOINT_TOKEN_STALE_TIME = 4 * 60 * 1000; // 4 min

                const { ssoToken, tokenlastRefresh, spUrl } = scalepointData;

                if (
                    ssoToken &&
                    spUrl &&
                    tokenlastRefresh &&
                    Date.now() - tokenlastRefresh > SCALEPOINT_TOKEN_STALE_TIME
                ) {
                    const newSSOToken = await refreshToken(appConfig, spUrl);
                    if (newSSOToken !== null) {
                        logger.logDebug('Successfully refreshed the Scalepoint token');
                        setFormValue(elementId, {
                            ...scalepointData,
                            ssoToken: newSSOToken,
                            tokenlastRefresh: Date.now()
                        });
                    } else {
                        setComponentState(Status.ERROR);
                    }
                }
            }
        }

        fetchSSOToken();
    }, [appConfig, elementId, scalepointData, setFormValue, componentState, IS_SCALEPOINT_CLOSED]);

    // once a token is fetched, use it to fetch the js script
    useEffect(() => {
        // skip this useEffect if the Scalepoint feature toggle is active
        if (IS_SCALEPOINT_CLOSED) {
            return;
        }

        if (scalepointData !== undefined && !scalepointData.error && componentState === Status.LOADING) {
            removeScalepointTags();

            const { ssoToken, spUrl, spToken } = scalepointData;
            if (!ssoToken || !spUrl || !spToken) {
                return;
            }
            const scriptURL = `${spUrl}?sso_token=${ssoToken}&widget=true&target=${SCALEPOINT_DIV_ID}`;

            const script = document.createElement('script');

            script.onload = () => {
                // the component is ready once this is loaded
                logger.logDebug('Successfully loaded the Scalepoint script');
                setComponentState(Status.OK);
            };
            script.onerror = () => {
                logger.logError('Could not get Scalepoint script');
                setComponentState(Status.ERROR);
            };
            script.src = scriptURL;

            document.getElementsByTagName('head')[0].appendChild(script);
        }
        // eslint-disable-next-line
    }, [scalepointData]);

    // how we update local data:
    // when the div/scalepoint widget is clicked the `clickCount` is incremented
    // and also when the componentState changes to OK
    // when that happens we call the api to fetch and update local data
    // this helps control when this component is valid
    useEffect(() => {
        // skip this useEffect if the Scalepoint feature toggle is active
        if (IS_SCALEPOINT_CLOSED) {
            return;
        }

        if (componentState !== Status.OK) {
            return;
        }

        if (scalepointData === undefined || scalepointData.error) {
            return;
        }

        const { spToken } = scalepointData;
        if (!spToken) {
            return;
        }

        setPollBasketItemsTimeout(spToken, 0);

        return () => clearPollBasketItemsTimeout();
        // eslint-disable-next-line
    }, [clickCount, componentState]);

    /**
     * As updating the app's basket item list is triggered from a click
     * sometimes the user does not reclick the widget after adding an item
     * causing the app to not get an updated basket items list to show on the resume page
     * poll the endpoint to try and get the most updated basket
     */
    const POLL_INTERVAL = 1 * 1000;
    const POLL_MAX_COUNT = 3;
    const setPollBasketItemsTimeout = (spToken: string, callCount: number) => {
        clearPollBasketItemsTimeout();
        pollBasketItemsTimeoutRef.current = setTimeout(() => pollBasketItems(spToken, callCount), POLL_INTERVAL);
    };
    const pollBasketItems = async (spToken: string, callCount: number = 0) => {
        if (callCount < POLL_MAX_COUNT) {
            const basketItems = await getBasketItems(appConfig, spToken);
            if (basketItems !== null) {
                logger.logDebug('Successfully get the Scalepoint basket');
                const itemsInBasketServerside = basketItems.lossItems;
                setFormValue(elementId, {
                    ...scalepointData,
                    scalepointItemsInBasket: itemsInBasketServerside.length ? itemsInBasketServerside : []
                });
                // no change in basket item list, fetch again after POLL_INVERVAL
                if (itemsInBasketServerside.length === scalepointData.scalepointItemsInBasket.length) {
                    setPollBasketItemsTimeout(spToken, callCount + 1);
                }
            } else {
                setComponentState(Status.ERROR);
            }
        }
    };
    const clearPollBasketItemsTimeout = () => {
        if (pollBasketItemsTimeoutRef.current) {
            clearTimeout(pollBasketItemsTimeoutRef.current);
            pollBasketItemsTimeoutRef.current = undefined;
        }
    };

    useEffect(() => {
        // skip this useEffect if the Scalepoint feature toggle is active
        if (IS_SCALEPOINT_CLOSED) {
            return;
        }

        if (componentState === Status.ERROR) {
            // we should allow customers to continue if there are errors with the widget
            setFormValue(elementId, { error: true });
        }
    }, [componentState, elementId, setFormValue, IS_SCALEPOINT_CLOSED]);

    return (
        <>
            {IS_SCALEPOINT_CLOSED ? (
                <Message theme="warning">
                    Vores genstandsregistrering er i øjeblikket ikke tilgængelig. Du kan fortsætte uden at registrere
                    dine genstande. Du vil modtage en mail med et link og en guide til, hvordan du registrerer dine
                    ting, når vi har modtaget din anmeldelse. Ofte inden for et par dage.
                </Message>
            ) : (
                <div className={`scalepoint relative mbFormGroup`} data-testid="scalepointwidget">
                    {componentState === Status.LOADING && <LoadingDots />}
                    {componentState === Status.OK && <p>Husk at klikke 'Tilføj genstand' efter hver ting.</p>}
                    {componentState === Status.ERROR && (
                        <Message theme="warning">
                            Vores genstandsregistrering er i øjeblikket ikke tilgængelig. Du kan fortsætte uden at
                            registrere dine genstande. Vi kontakter dig for at få de manglende oplysninger.
                        </Message>
                    )}
                    {/* {!isFetching && props.validationStatus === 'error' && (
                <p className={`textError`}>
                    {scalepointItemsInBasket.length === 0 ? `Udfyld feltet` : `Der opstod en fejl`}
                </p>
            )}*/}
                    {/* eslint-disable-next-line */}
                    <div onClick={() => setClickCount((prevCount) => prevCount + 1)} id={SCALEPOINT_DIV_ID} />
                </div>
            )}
        </>
    );
};

const removeScalepointTags = () => {
    // the scalepoint script uses a onload callback to render the widget
    // that forces us to have to always load the script each time the scalepoint widget needs to be displayed
    // so we should try and cleanup the previous scripts/links added before this (if any)
    const scripts = Array.from(document.getElementsByTagName('head')[0].getElementsByTagName('script'));
    for (let script of scripts) {
        if (script.src.includes('scalepoint.com')) {
            script.parentNode?.removeChild(script);
        }
    }

    // when the script is loaded, it loads some css too
    const links = Array.from(document.getElementsByTagName('head')[0].getElementsByTagName('link'));
    for (let link of links) {
        if (link.type === 'text/css' && link.rel === 'stylesheet' && link.href.includes('scalepoint.com')) {
            link.parentNode?.removeChild(link);
        }
    }

    // the scalepoint script also adds alot of style tags that cannot be referred to and removed
};

const ScalepointWidgetAgeGroup = (itemAge: number) => {
    if (itemAge < 6) return 'Under seks måneder';
    if (itemAge < 12) return 'Mindre end et år';
    if (itemAge < 24) return '1-2 år';
    if (itemAge < 36) return '2-3 år';
    if (itemAge < 48) return '3-4 år';
    if (itemAge < 60) return '4-5 år';
    if (itemAge < 72) return '5-6 år';
    if (itemAge < 84) return '6-7 år';
    if (itemAge < 96) return '7-8 år';
    if (itemAge < 108) return '8-9 år';
    if (itemAge < 120) return '9-10 år';
    if (itemAge < 132) return '10-11 år';
    if (itemAge < 144) return '11-12 år';
    if (itemAge < 156) return '12-13 år';
    if (itemAge < 168) return '13-14 år';
    if (itemAge < 180) return '14-15 år';
    if (itemAge < 192) return '15-16 år';
    if (itemAge < 204) return '16-17 år';
    if (itemAge < 216) return '17-18 år';
    return 'Over 18 år';
};

Scalepoint.getValueHuman = (formValues: FormValues, elementId: string) => {
    const valueObj: ScalepointData = Scalepoint.getValue(formValues, elementId);

    let str = '';

    const itemsInBasket = valueObj?.scalepointItemsInBasket ?? [];

    itemsInBasket.forEach((r) => {
        str += `Beskrivelse: ${r.description}\n`;
        str += `Alder: ${ScalepointWidgetAgeGroup(r.age)}\n`;
        str += `Nypris: ${r.newPrice}\n`;
        str += `Krav: ${r.customerDemand}\n\n`;
    });

    return str;
};

Scalepoint.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    const element = getElementFromElementId(formConfig, elementId) as ScalepointType;
    const { summaryCaption } = element;
    const valueObj: ScalepointData = Scalepoint.getValue(formValues, elementId);

    if (valueObj.error) {
        return null;
    }

    const itemsInBasket = valueObj?.scalepointItemsInBasket ?? [];
    if (element.allowEmptyBasket && itemsInBasket.length === 0) {
        return null;
    }

    return (
        <SummaryRowTemplate
            caption={summaryCaption}
            elementId={elementId}
            value={itemsInBasket.map(function (r) {
                return (
                    <div key={r.token}>
                        <b>Beskrivelse: </b>
                        {r.description}
                        <br />
                        <br />
                        <b>Alder: </b>
                        {ScalepointWidgetAgeGroup(r.age)}
                        <br />
                        <br />
                        <b>Nypris: </b>
                        {r.newPrice}
                        <br />
                        <br />
                        <b>Krav: </b>
                        {r.customerDemand}
                        <br />
                        <br />
                        <br />
                    </div>
                );
            })}
        />
    );
};

Scalepoint.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string) => {
    if (formValues[KEY_IS_SCALEPOINT_CLOSED]) {
        return true;
    }

    const value: ScalepointData = Scalepoint.getValueOrDefault(formValues, elementId);

    if (value.error) {
        //  allow to pass if widget error
        return true;
    }

    const elementConfig = getElementFromElementId(formConfig, elementId) as ScalepointType;

    if (elementConfig.allowEmptyBasket === true) {
        return true;
    }

    if (value.scalepointItemsInBasket.length) {
        return true;
    }

    return false;
};

Scalepoint.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const valueObj: ScalepointData = Scalepoint.getValue(formValues, elementId);

    if (valueObj === undefined || valueObj?.error) {
        return {
            spToken: '',
            spUrl: '',
            ssoToken: '',
            tokenlastRefresh: 0,
            scalepointItemsInBasket: [],
            error: valueObj?.error
        };
    }

    return valueObj;
};

Scalepoint.getValue = (formValues: FormValues, elementId: string) => {
    return formValues[elementId];
};

Scalepoint.getValueSubmit = (formValues: FormValues, elementId: string, caption: string) => {
    const valueObj = Scalepoint.getValueOrDefault(formValues, elementId);
    const valueStr = Scalepoint.getValueHuman(formValues, elementId);
    const valueId = omit(valueObj, 'tokenlastRefresh', 'error');

    let returnObj = getClaimFormSubmitValue('scalepointwidget', elementId, caption, valueStr, valueId);

    // with the Scalepoint feature toggle, it is possbile to turn off the Scalepoint widget (e.g. if it is down or under maintenance)
    // we must tell the CA backend when it is closed, such that it can tell Scalepoint to send the customer an email to collect the missing item(s) information,
    // hence the isScalepointClosed flag is always appended to the submitted form data
    if (formValues[KEY_IS_SCALEPOINT_CLOSED]) {
        assign(returnObj.valueID, { isScalepointClosed: true });
    } else {
        assign(returnObj.valueID, { isScalepointClosed: false });
    }

    return returnObj;
};

export default Scalepoint;
